import * as Sentry from '@sentry/react';
import { type QueryClient, type UseQueryResult, useQuery } from 'react-query';
import { type DisplaySettings, loadDisplaySettings } from '@packages/display-policy';
import { DisplayConfigurationSharedService } from '../../services/wealthlane-identity-services';
import { getTenantId } from '../../utils/tenantHelpers';

// HTF-1482: company money display precision (2 | 4).
// Unlike other useGet* hooks this does NOT use handleRequest: a failure must fall back to 4
// with no toast / error modal. It is reported to Sentry instead, so a broken endpoint stays visible.
// Token + X-Tenant-Id come from the identity client's OpenAPI.TOKEN / OpenAPI.HEADERS, set by
// useWealthlaneIdentityApi (rendered by App and BrandingProvider) — same as the branding request.

const displaySettingsQueryKey = (tenantId?: string | null) => ['displaySettings', tenantId];

// src/queryClient.ts defaults to cacheTime 0; keep the value for the whole session.
const queryOptions = { retry: false, staleTime: Infinity, cacheTime: Infinity } as const;

export const fetchDisplaySettings = (): Promise<DisplaySettings> =>
  loadDisplaySettings(async () => {
    try {
      return await DisplayConfigurationSharedService.getApiIdentityApiAppDisplayConfigurationSharedCurrent();
    } catch (error) {
      Sentry.captureMessage('[HTF-1482] display settings fallback to 4', {
        level: 'warning',
        extra: { tenantId: getTenantId(), error: String(error) },
      });
      throw error;
    }
  });

/** Starts the request with branding (same token + tenant condition) so the gate does not wait for it. */
export const prefetchDisplaySettings = (
  client: QueryClient,
  tenantId?: string | null
): Promise<void> =>
  client.prefetchQuery(displaySettingsQueryKey(tenantId), fetchDisplaySettings, queryOptions);

const useGetDisplaySettings = (
  tenantId?: string | null,
  enabled: boolean = true
): UseQueryResult<DisplaySettings, unknown> =>
  useQuery(displaySettingsQueryKey(tenantId), fetchDisplaySettings, { ...queryOptions, enabled });

export default useGetDisplaySettings;
